import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Typography,
} from '@mui/material';
import { registerConflictHost } from '../../utils/conflict';

/**
 * #579: the app-level "someone else changed this" prompt for the NON-EDITOR update
 * flows (card quick-edit, AI functions, tree patch, tree rename).
 *
 * Mounted once by `App`, because three of those flows live in hooks or inline
 * callbacks that render no JSX of their own. `saveWithConflictPrompt` calls into it
 * through `registerConflictHost` and awaits the user's choice.
 *
 * Two options, deliberately — not the editor's field-level merge (#161). These flows
 * change one thing (a title, a description, an attribute), so "whose version wins"
 * is the whole decision; a per-field merge UI would be ceremony over a rename.
 */
export default function ConflictPromptHost() {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);

  useEffect(() => registerConflictHost((info) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setRequest(info);
  })), []);

  const answer = useCallback((choice) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    if (resolve) resolve(choice);
  }, []);

  if (!request) return null;

  const theirTitle = request.current?.title;

  return (
    <Dialog
      open
      // Dismissing without choosing is the SAFE answer, not the destructive one:
      // Escape / backdrop discards the local edit rather than overwriting theirs.
      onClose={() => answer('reload')}
      maxWidth="sm"
      fullWidth
      // #627 (2026-07-27): this carried `sx={{ zIndex: 3000 }}` to climb over the
      // floating item-card window, which painted at `zIndex: 2000 + z` — above MUI's
      // modal layer (1300). The symptom was found by the e2e spec driving the card
      // quick-edit conflict: the prompt RENDERED correctly but its buttons were
      // unclickable, Playwright reporting `.ic-lab ... intercepts pointer events`.
      //
      // The workaround is REMOVED because the root cause is fixed: cards now paint
      // BELOW the modal layer (`utils/zLayers.js`). Keeping it would be actively
      // harmful — 3000 is above MUI's modal layer, so this one dialog would outrank
      // every OTHER dialog — and it was never durable anyway: the card counter it
      // was escaping is monotonic and unbounded, so enough card focus events would
      // have sailed past 3000 too. The layer that must win is `modal`, and MUI's
      // default already is that.
      data-testid="conflict-prompt"
      aria-labelledby="conflict-prompt-title"
    >
      <DialogTitle id="conflict-prompt-title">This item changed while you were editing</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography variant="body2" sx={{ mb: theirTitle ? 1.5 : 0 }}>
            Someone else saved <strong>{request.label}</strong> since you loaded it, so your
            change was not applied. Reload to take their version, or keep yours and save
            it over the top.
          </Typography>
          {theirTitle && (
            <Typography variant="body2" color="text.secondary" data-testid="conflict-prompt-theirs">
              Their current version: “{theirTitle}”
            </Typography>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => answer('reload')} data-testid="conflict-reload">
          Reload theirs
        </Button>
        <Button
          onClick={() => answer('keep')}
          variant="contained"
          color="warning"
          data-testid="conflict-keep-mine"
        >
          Keep mine
        </Button>
      </DialogActions>
    </Dialog>
  );
}
